/**
 * Jetons de design utiles au TypeScript (points de rupture, conteneurs).
 * Source unique : `tokens.json` ; les couleurs, espacements et rayons ne se
 * lisent qu'en CSS (`var(--…)`), jamais recopiés ici.
 */
import { useSyncExternalStore } from 'react';
import tokens from './tokens.json';

export type Breakpoint = keyof typeof tokens.breakpoints;

/** Largeurs minimales, en px : 600 (sm), 900 (md, bascule de la coque), 1200 (lg). */
export const breakpoints: Readonly<Record<Breakpoint, number>> = tokens.breakpoints;

/** Requêtes média prêtes pour `matchMedia`, alignées sur le CSS. */
export const media: Readonly<Record<Breakpoint, string>> = {
  sm: `(min-width: ${tokens.breakpoints.sm}px)`,
  md: `(min-width: ${tokens.breakpoints.md}px)`,
  lg: `(min-width: ${tokens.breakpoints.lg}px)`,
};

/** Vrai au-dessus d'un point de rupture (faux hors navigateur). */
export function matchesBreakpoint(name: Breakpoint): boolean {
  return typeof window !== 'undefined' && window.matchMedia(media[name]).matches;
}

/** Vrai tant que la requête média correspond, et suivi quand la fenêtre change de taille. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (notify) => {
      if (typeof window === 'undefined') return () => undefined;
      const list = window.matchMedia(query);
      list.addEventListener('change', notify);
      return () => list.removeEventListener('change', notify);
    },
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
    () => false,
  );
}

/** Vrai au-dessus d'un point de rupture, et suivi quand la fenêtre change de taille. */
export function useBreakpoint(name: Breakpoint): boolean {
  return useMediaQuery(media[name]);
}
